import { useCallback, useEffect, useState, type FormEvent } from 'react'
import type { AssessmentStatus, MetaResponse, PatientSummary, SemblePatientHit } from '../../shared/types'
import { api } from '../api'
import { Card, ErrorNote, Pill, Spinner } from '../components'
import { dateLabel } from '../format'
import { useSession } from '../session-context'

const STATUS_LABEL: Record<AssessmentStatus, string> = { draft: 'Draft', in_review: 'In review', signed: 'Signed', delivered: 'In Semble' }
const STATUS_TONE: Record<AssessmentStatus, 'ok' | 'warn' | 'bad' | 'neutral'> = { draft: 'neutral', in_review: 'warn', signed: 'ok', delivered: 'ok' }

export function PatientsScreen({ onOpenAssessment, onOpenPatient, meta }: { onOpenAssessment: (id: number) => void; onOpenPatient: (id: number) => void; meta: MetaResponse | null }) {
  const { can } = useSession()
  const [patients, setPatients] = useState<PatientSummary[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [q, setQ] = useState('')
  const [programmeOnly, setProgrammeOnly] = useState(false)
  const [hits, setHits] = useState<SemblePatientHit[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => setPatients((await api.patients()).patients), [])
  useEffect(() => { load().catch(setError) }, [load])

  async function search(e: FormEvent) {
    e.preventDefault()
    setSearching(true); setError(null)
    try { setHits((await api.searchSemble(q, programmeOnly)).hits) } catch (err) { setError(err) } finally { setSearching(false) }
  }

  async function start(patientId: number) {
    setBusy(`start-${patientId}`)
    try {
      const a = await api.createAssessment(patientId, new Date().toISOString().slice(0, 10))
      onOpenAssessment(a.id)
    } catch (err) { setError(err) } finally { setBusy(null) }
  }

  async function link(h: SemblePatientHit) {
    setBusy(h.sembleId)
    try {
      const p = await api.linkPatient(h.sembleId)
      await load()
      setHits(null); setQ('')
      await start(p.id)
    } catch (err) { setError(err) } finally { setBusy(null) }
  }

  if (patients === null) return <Spinner />
  return (
    <>
      <ErrorNote error={error} />
      {can('nurse') && (
        <Card title="Find a patient in Semble" subtitle={meta?.semble.enabled === true ? `Searching ${meta.semble.sandbox ? 'the sandbox' : 'live Semble'} by name, email or date of birth` : 'Semble is not configured on this service'}>
          <form className="row" onSubmit={(e) => void search(e)}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Surname, email or DOB" disabled={meta?.semble.enabled !== true} />
            <label className="check"><input type="checkbox" checked={programmeOnly} onChange={(e) => setProgrammeOnly(e.target.checked)} /> Only patients labelled “{meta?.semble.programmeLabel ?? 'Prevent51'}”</label>
            <button type="submit" disabled={searching || meta?.semble.enabled !== true}>{searching ? 'Searching…' : 'Search'}</button>
          </form>
          {hits !== null && (hits.length === 0 ? <p className="note">No matches.</p> : (
            <table>
              <thead><tr><th>Name</th><th>DOB</th><th>Email</th><th>Labels</th><th></th></tr></thead>
              <tbody>
                {hits.map((h) => (
                  <tr key={h.sembleId}>
                    <td>{h.lastName}, {h.firstName}</td><td>{dateLabel(h.dob)}</td><td className="muted">{h.email ?? ''}</td>
                    <td>{h.labels.map((l) => <Pill key={l} tone="neutral">{l}</Pill>)}</td>
                    <td>{h.localId !== null ? <button type="button" onClick={() => void start(h.localId!)} disabled={busy !== null}>New assessment</button>
                      : <button type="button" onClick={() => void link(h)} disabled={busy !== null}>{busy === h.sembleId ? 'Linking…' : 'Bring in and start'}</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </Card>
      )}

      <Card title="Patients in the portal" subtitle="Click a name for the patient's page and trends, or a date to open that assessment.">
        {patients.length === 0 ? <p className="note">No patients yet. Search Semble above to bring one in.</p> : (
          <table>
            <thead><tr><th>Patient</th><th>DOB</th><th>Assessments</th><th></th></tr></thead>
            <tbody>
              {patients.map((p) => (
                <tr key={p.id}>
                  <td><button type="button" className="linkish" onClick={() => onOpenPatient(p.id)}><b>{p.lastName}, {p.firstName}</b></button>{p.sembleId !== null && <div className="muted small">Semble {p.sembleId}</div>}</td>
                  <td>{dateLabel(p.dob)}</td>
                  <td>
                    {p.assessments.length === 0 ? <span className="muted">none</span> : p.assessments.map((a) => (
                      <div key={a.id} className="row-line">
                        <button type="button" className="linkish" onClick={() => onOpenAssessment(a.id)}>{dateLabel(a.assessmentDate)}</button>
                        <Pill tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Pill>
                        <span className="muted small">{a.reportType === 'summary' ? 'short report · ' : ''}{a.resultCount} results{a.latestVersion !== null && ` · v${a.latestVersion}`}</span>
                      </div>
                    ))}
                  </td>
                  <td>{can('nurse') && <button type="button" onClick={() => void start(p.id)} disabled={busy !== null}>New assessment</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}
